import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = 'https://opmlee572h.execute-api.us-west-2.amazonaws.com';

function BookingSuccess() {
  const navigate = useNavigate();
  const [booking,   setBooking]   = useState(null);
  const [countdown, setCountdown] = useState(8);

  const [refunded, setRefunded] = useState(false);

  useEffect(() => {
    // Pull session_id from URL if Stripe redirect
    const params     = new URLSearchParams(window.location.search);
    const sessionId  = params.get('session_id');
    const tab        = params.get('tab') || 'bookings';

    // Try to fetch booking details from session
    if (sessionId) {
      // Wait 3s for webhook to process, then check
      setTimeout(() => {
        fetch(`${API_URL}/booking-by-session?sessionId=${sessionId}`)
          .then(r => r.json())
          .then(data => {
            if (data && data.date) {
              setBooking(data);
            } else if (data?.refunded) {
              setRefunded(true);
            }
          })
          .catch(() => {});
      }, 3000);
    }

    // Countdown then redirect
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          navigate(`/client-dashboard?tab=${tab}`);
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [navigate]);

  const handleAddToCalendar = () => {
    if (!booking) return;
    const startDate  = booking.date.replace(/-/g, '');
    const title      = encodeURIComponent(`${booking.service?.name || 'Appointment'} with ${booking.providerName || 'Provider'}`);
    const details    = encodeURIComponent(`Booked via Vyblo`);
    const googleUrl  = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}/${startDate}&details=${details}`;
    window.open(googleUrl, '_blank');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--cream)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      fontFamily: 'DM Sans, sans-serif',
    }}>
      <div style={{ width: '100%', maxWidth: '520px', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

        {/* Main card */}
        <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '24px', overflow: 'hidden', boxShadow: 'var(--shadow-lg)' }}>

          {/* Hero — green for success, red for refund */}
          <div style={{ background: refunded ? '#dc2626' : 'var(--green)', padding: '3rem 2.5rem', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, background: 'radial-gradient(circle, rgba(255,255,255,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, background: `radial-gradient(circle, ${refunded ? 'rgba(220,38,38,0.3)' : 'rgba(74,158,107,0.4)'} 0%, transparent 70%)`, pointerEvents: 'none' }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: '2px solid rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem', fontSize: '2rem' }}>
                {refunded ? '↩' : '✓'}
              </div>
              <p style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>
                {refunded ? '⚠️ Duplicate Appointment Detected' : "You're all set"}
              </p>
              <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '2rem', fontWeight: 800, color: 'white', lineHeight: 1.15, marginBottom: '0.5rem' }}>
                {refunded ? 'Heads up!' : 'Booking Confirmed!'}
              </h1>
              <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem', maxWidth: '360px', margin: '0 auto' }}>
                {refunded
                  ? 'Looks like you already have an upcoming appointment for this service. No worries — your payment will automatically go back into your account within 5-10 business days.'
                  : 'Your appointment has been booked successfully.'}
              </p>
            </div>
          </div>

          {/* Booking details (shown if we have them) */}
          {booking && (
            <div style={{ padding: '1.75rem 2rem', borderBottom: '1px solid var(--border)' }}>
              <p style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.875rem' }}>Appointment Details</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {[
                  { icon: '✂', label: 'Service',  value: booking.service?.name || 'Appointment' },
                  { icon: '👤', label: 'Provider', value: booking.providerName || '—' },
                  { icon: '📅', label: 'Date',     value: booking.date },
                  { icon: '⏰', label: 'Time',     value: booking.slot },
                  { icon: '💳', label: 'Amount',   value: `$${booking.service?.price || '—'}` },
                ].map((row, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', background: 'var(--cream)', borderRadius: '8px' }}>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span>{row.icon}</span>{row.label}
                    </span>
                    <span style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text)' }}>{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ padding: '1.75rem 2rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <button
              onClick={() => navigate('/client-dashboard?tab=bookings')}
              style={{ background: 'var(--green)', color: 'white', border: 'none', borderRadius: '10px', padding: '0.9rem', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', width: '100%', transition: 'all 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--green-dark)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--green)'}
            >
              View My Bookings →
            </button>

            {booking && (
              <button
                onClick={handleAddToCalendar}
                style={{ background: 'var(--cream)', color: 'var(--text-mid)', border: '1.5px solid var(--border)', borderRadius: '10px', padding: '0.9rem', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', width: '100%', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(26,92,58,0.3)'; e.currentTarget.style.color = 'var(--green)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-mid)'; }}
              >
                📅 Add to Google Calendar
              </button>
            )}

            <button
              onClick={() => navigate('/client-dashboard')}
              style={{ background: 'transparent', color: 'var(--text-muted)', border: 'none', borderRadius: '10px', padding: '0.75rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', width: '100%' }}
            >
              Back to Browse
            </button>
          </div>
        </div>

        {/* Countdown + redirect notice */}
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            Redirecting to your bookings in <strong style={{ color: 'var(--green)' }}>{countdown}s</strong>
          </p>
        </div>

        {/* Vyblo branding */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.25rem', fontWeight: 800, color: 'var(--green)', letterSpacing: '-0.02em' }}>
            Vyb<span style={{ color: 'var(--text-muted)' }}>lo</span>
          </div>
          <p style={{ fontSize: '0.72rem', color: 'var(--text-light)', marginTop: '0.25rem' }}>Your booking platform</p>
        </div>

      </div>
    </div>
  );
}

export default BookingSuccess;
